<template>
  <div class="timeline-container box">
    <template v-if="showTimeline">
      <b-steps v-model="currentStep" :has-navigation="false"
               :label-position="labelPosition" :mobile-mode="'minimalist'"
               :position="position"
               class="is-align-self-center steps" horizontal
               size="is-small"
               type="is-success">
        <template v-for="(step, index) in timelineSteps[currentWorkflow]">
          <b-step-item v-if="step.included !== false" :key="index" :clickable="false"
                       :icon="getStepIcon(step)"
                       :label="step.label"
                       :rounded="false"
                       :value="step.label"
                       class="step-content"
          >
            <div v-if="!deviceIsMobile" class="current-step-description">
              {{ step.text }}
            </div>
          </b-step-item>
        </template>
      </b-steps>
    </template>
    <template v-else>
      <div class="current-step-description placeholder-status">
        This project has no timeline.
      </div>
    </template>
    <div class="separator"></div>
    <div class="due-date-container">
      <b-field label="Due Date" custom-class="has-text-black is-size-5" expanded>
        <p class="control is-size-6">
          {{ estimateDueDateFormatted }}
        </p>
      </b-field>
    </div>
  </div>
</template>

<script>
import {DECLINED} from "../../module/constants";
import filters from 'module/filters';

const WORKFLOW_LABELS = {
  qto: 'quantityTakeOff',
  ito: 'internalTakeOff'
}

export default {
  name: "project-timeline",
  filters,
  props: {
    labelPosition: {
      type: String,
      default: 'bottom'
    },
    position: {
      type: String,
      default: 'left'
    }
  },
  data() {
    return {
      showTimeline: true,
      currentWorkflow: null,
      currentStep: 0,
      timelineSteps: {
        qto: [
          {
            label: 'Checking Project Details',
            name: 'checkingProjectDetails',
            isComplete: false,
            included: true,
            internalSteps: ['initialEnquiry', 'quoteAnalysis', 'generatePlanswiftJob'],
            text: 'The plans and details that have been submitted are being reviewed. We will be in touch if we need any further information.'
          },
          {
            label: 'Estimator Review',
            name: 'estimatorReview',
            isComplete: false,
            included: true,
            internalSteps: ['estimatorReview'],
            text: 'Your estimator is reviewing your plans, confirming that he has all the information required for an accurate estimate.'
          },
          {
            label: 'Creating the Estimate',
            name: 'creatingYourEstimate',
            isComplete: false,
            included: true,
            internalSteps: ['createEstimate'],
            text: 'Your estimator is now constructing your estimate.'
          },
          {
            label: 'Builder Review',
            name: 'builderReview',
            isComplete: false,
            included: true,
            internalSteps: ['builderReview'],
            text: 'Your estimator will be calling you to review your estimate.'
          },
          {
            label: 'Amending the Estimate',
            name: 'amendingEstimate',
            isComplete: false,
            included: false,
            internalSteps: ['amendQuote', 'amendQuoteFromSap'],
            text: 'Your estimator is now amending your estimate.'
          },
          {
            label: 'Preparing the Reports',
            name: 'preparingYourReports',
            isComplete: false,
            included: true,
            internalSteps: ['generateReports', 'reGenerateReports', 'confirmReports', 'confirmAmendedReports'],
            text: 'Your reports are now being prepared. Once finished, Quality Control will check them and we will be contacting you.'
          },
          {
            label: 'Reports Ready',
            name: 'reportsReady',
            isComplete: false,
            included: true,
            internalSteps: [],
            text: 'Your reports are ready. Please review them and confirm that you are happy with them.'
          }
        ],
        ito: [
          {
            label: 'Checking Project Details',
            name: 'checkingProjectDetails',
            isComplete: false,
            included: true,
            internalSteps: ['initialEnquiry', 'generatePlanswiftJob'],
            text: 'The plans and details that have been submitted are being reviewed. We will be in touch if we need any further information.'
          },
          {
            label: 'Creating the Estimate',
            name: 'creatingTheEstimate',
            isComplete: false,
            included: true,
            internalSteps: ['createEstimate'],
            text: 'The estimator is now constructing the estimate.'
          },
          {
            label: 'Click Ready',
            name: 'clickReady',
            isComplete: false,
            included: true,
            internalSteps: ['clickLinkSent'],
            text: 'Your estimate is ready. A Click link has been sent to your email inbox.'
          }
        ]
      }
    }
  },
  computed: {
    deviceIsMobile() {
      return this.$store.getters['device/deviceType'] === 'mobile';
    },
    project() {
      return this.$store.getters['quote/selectedQuote'];
    },
    isExternalUser() {
      return this.$store.getters['user/isExternalCompany'];
    },
    estimateDueDate() {
      return _.get(this.project, `workflow.${WORKFLOW_LABELS[this.currentWorkflow]}.date.due`, null);
    },
    estimateDueDateFormatted() {
      return this.estimateDueDate ? filters.date(this.estimateDueDate, 'DD/MM/YYYY') : 'TBC';
    },
  },
  beforeMount() {
    this.setTimelineSteps();
  },
  methods: {
    getStepIcon(step) {
      if (step.isComplete) {
        return 'check';
      } else {
        return '';
      }
    },
    setTimelineSteps() {
      const qto = _.get(this.project, 'workflow.quantityTakeOff', null);
      const ito = _.get(this.project, 'workflow.internalTakeOff', null);

      if (qto === null && ito === null) {
        this.showTimeline = false;
        return;
      }

      const qtoDeclined = _.get(qto, 'state.state', DECLINED) === DECLINED;
      const itoDeclined = _.get(ito, 'state.state', DECLINED) === DECLINED;

      if (qtoDeclined && itoDeclined) {
        this.showTimeline = false
        return;
      }

      const workflow = qtoDeclined ? ito : qto;
      this.currentWorkflow = qtoDeclined ? 'ito' : 'qto';

      this.showTimeline = true;

      const previousWorkflowSteps = _.get(workflow, 'steps.previous', []);

      _.forEach(previousWorkflowSteps, (step) => {
        const stepName = step.name;
        const stepStatus = step.state;

        _.forEach(this.timelineSteps[this.currentWorkflow], (timelineStep) => {
          if (timelineStep.internalSteps.includes(stepName)) {
            timelineStep.isComplete = stepStatus === '5_COMPLETE';

            if (stepName === 'amendQuote' || stepName === 'amendQuoteFromSap') {
              timelineStep.included = true;
            }
          }
        });

        if (_.get(workflow, 'steps.next', []).length === 0) {
          _.forEach(this.timelineSteps[this.currentWorkflow], (timelineStep) => {
            if (timelineStep.name === 'reportsReady' || timelineStep.name === 'clickReady') {
              timelineStep.isComplete = true;
            }
          });
        }
      });

      const currentIndex = this.timelineSteps[this.currentWorkflow].findIndex(step => step.included && !step.isComplete);
      this.currentStep = currentIndex !== -1 ? currentIndex : this.timelineSteps[this.currentWorkflow].length - 1;
    },

  }
}
</script>

<style lang="scss" scoped>
.timeline-container {
  display: flex;
  height: 100%;
  min-height: 133px;
  width: 100%;
  align-items: center;
  padding: 1em;
  max-height: 100%;
  margin-bottom: 0;
  flex-direction: row;
  justify-content: space-between;

  & > :first-child {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    flex: 8;
  }

  .separator {
    height: 100%;
    width: 1px;
    background-color: $tertiary;
    opacity: 0.2;
  }

  .due-date-container {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
  }
}

.tooltip {
  visibility: hidden;
  background-color: black;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  margin-left: -60px;
  opacity: 0;
  transition: opacity 0.3s;
}

.b-step-item:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.current-step-description {
  color: $primary-font-color;
  font-size: 1.250rem;
  font-weight: 500;
  text-align: center;
}

.control {
  color: $tertiary;
}

.content-section {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;

  .tracker {
    margin: 2em 0 0 0;

    .steps {
      margin-left: 2em;
    }
  }

  .step-content {
    //height: calc(100vh - 120px);
    overflow: auto;
    background-color: whitesmoke;
  }
}
</style>